import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SEO from '../components/SEO';

const AboutUs = () => {
  const { t } = useTranslation();

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeInOut" },
    },
  };

  // Hook for each section
  const [teamRef, teamInView] = useInView({ threshold: 0.2 });
  const [missionRef, missionInView] = useInView({ threshold: 0.2 });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.2 });

  return (
    <>
      <SEO 
        path="/about"
        title={t("aboutUs.title")}
        description={t("aboutUs.missionDescription")}
        keywords="DamenKYT, blockchain security, compliance, risk monitoring, about us"
        ogTitle={t("aboutUs.title")}
        ogDescription={t("aboutUs.missionDescription")}
        ogImage="/images/aboutUsImg1.png"
        twitterCard="summary_large_image"
        twitterTitle={t("aboutUs.title")}
        twitterDescription={t("aboutUs.missionDescription")}
        twitterImage="/images/aboutUsImg1.png"
        canonicalUrl={window.location.href}
        robots="index, follow"
        siteName="DamenKYT"
        image="/images/aboutUsImg1.png"
        imageWidth="1200"
        imageHeight="630"
        imageAlt={t("aboutUs.title")}
      />
      <div className="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-900 dark:to-gray-800 min-h-screen text-gray-800 dark:text-gray-200 font-sans overflow-hidden">
        {/* Banner */}
        <div
          className="w-full h-64 bg-cover bg-center flex items-center justify-center text-white text-3xl font-bold"
          style={{ backgroundImage: `url('/images/myReportsBanner.png')` }}
        >
          {t("aboutUs.title")}
        </div>

        <div className="container mx-auto px-6 py-24 relative">
          {/* 團隊介紹 */}
          <motion.div
            ref={teamRef}
            initial="hidden"
            animate={teamInView ? "visible" : "hidden"}
            variants={fadeIn}
            className="flex flex-col md:flex-row justify-center items-center gap-16 mb-24"
          >
            <div className="md:w-1/2">
              <h2 className="text-3xl font-semibold mb-6 tracking-tight leading-tight">
                {t("aboutUs.teamTitle")}
              </h2>
              <p className="text-lg leading-relaxed text-gray-600 dark:text-gray-400">
                {t("aboutUs.teamDescription")}
              </p>
            </div>
            <div className="md:w-1/2 relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 rounded-2xl blur-lg filter group-hover:opacity-30 transition duration-300"></div>
              <img
                src="/images/aboutUsImg1.png"
                alt="aboutUsImg1"
                className="rounded-2xl shadow-lg object-cover w-full h-96 transition duration-300 group-hover:scale-105"
              />
            </div>
          </motion.div>

          {/* 我們的任務 */}
          <motion.div
            ref={missionRef}
            initial="hidden"
            animate={missionInView ? "visible" : "hidden"}
            variants={fadeIn}
            className="mb-24"
          >
            <h2 className="text-4xl font-bold text-center mb-12 tracking-tight leading-tight">
              {t("aboutUs.missionTitle")}
            </h2>
            <div className="flex flex-col md:flex-row items-center max-w-3xl mx-auto gap-6">
              <div className="w-full md:w-1/2 relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 rounded-2xl blur-lg filter group-hover:opacity-30 transition duration-300"></div>
                <img
                  src="/images/aboutUsImg2.png"
                  alt="About Us Mission"
                  className="rounded-2xl shadow-lg object-cover w-full h-96 transition duration-300 group-hover:scale-105"
                />
              </div>
              <div className="w-full md:w-1/2">
                <p className="text-lg leading-relaxed text-gray-600 dark:text-gray-400">
                  {t("aboutUs.missionDescription")}
                </p>
              </div>
            </div>
          </motion.div>

          {/* 功能介紹 */}
          <motion.div
            ref={featuresRef}
            initial="hidden"
            animate={featuresInView ? "visible" : "hidden"}
            variants={fadeIn}
          >
            <h2 className="text-4xl font-bold text-center mb-12 tracking-tight leading-tight">
              {t("aboutUs.featuresTitle")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:scale-105 transition duration-300">
                <h3 className="text-2xl font-semibold mb-4 tracking-tight leading-tight">
                  {t("aboutUs.featureAMLTitle")}
                </h3>
                <p className="text-lg leading-relaxed text-gray-600 dark:text-gray-400">
                  {t("aboutUs.featureAMLDescription")}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:scale-105 transition duration-300">
                <h3 className="text-2xl font-semibold mb-4 tracking-tight leading-tight">
                  {t("aboutUs.featureReportSupportTitle")}
                </h3>
                <p className="text-lg leading-relaxed text-gray-600 dark:text-gray-400">
                  {t("aboutUs.featureReportSupportDescription")}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default AboutUs;
